import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './i18n';
import PublicProviders from './components/PublicProviders';
import AppProviders from './components/AppProviders';
import PrivateRoute from './components/PrivateRoute';
import ErrorBoundary from './components/ErrorBoundary';
import Landing from './pages/public/Landing';
// Loaded eagerly (not lazy) so the footer/header "FAQ" links navigate with no
// chunk fetch and no loading flash — the page is tiny.
import FAQ from './pages/public/FAQ';
import OurStory from './pages/public/OurStory';
import Checkout from './pages/public/Checkout';
import PaymentCheckout from './pages/public/PaymentCheckout';
import PaymentSuccess from './pages/public/PaymentSuccess';
import PaymentPending from './pages/public/PaymentPending';
import PaymentFailed from './pages/public/PaymentFailed';
import SecureCheckout from './pages/public/SecureCheckout';
import DemoCredentials from './pages/public/DemoCredentials';
import DemoActivation from './pages/public/DemoActivation';
import PortalAuth from './pages/public/PortalAuth';
import PortalDashboard from './pages/public/PortalDashboard';
import ScrollToTop from './components/ScrollToTop';
import VisitTracker from './components/VisitTracker';
import AcceptInvitation from './pages/AcceptInvitation';
import NotFound from './pages/NotFound';
import Login from './pages/Login';

const lazyWithRetry = (componentImport, chunkName) => lazy(() =>
  componentImport()
    .then((component) => {
      try {
        sessionStorage.removeItem(`lazy-retry-${chunkName}`);
      } catch {}
      return component;
    })
    .catch((error) => {
      const isChunkLoadError = error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk');
      const retryKey = `lazy-retry-${chunkName}`;
      let alreadyRetried = true;

      try {
        alreadyRetried = sessionStorage.getItem(retryKey) === '1';
      } catch {}

      if (isChunkLoadError && !alreadyRetried) {
        try {
          sessionStorage.setItem(retryKey, '1');
        } catch {}
        window.location.reload();
        return new Promise(() => {});
      }

      throw error;
    })
);

// Layout (lazy-loaded — pulls in all app modules only when user enters /app)
const MainLayout = lazyWithRetry(() => import('./components/Layout/MainLayout'), 'main-layout');

// Public pages lazy-loaded to keep the initial bundle small
const Pricing = lazyWithRetry(() => import('./pages/public/Pricing'), 'pricing');
const PatientPortal = lazyWithRetry(() => import('./pages/public/PatientPortal'), 'patient-portal');
const ForgotPassword = lazyWithRetry(() => import('./pages/ForgotPassword'), 'forgot-password');
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'), 'reset-password');
// Tutorials page temporarily disabled — still has mockup data. Re-enable when ready.
// const Tutorials = lazy(() => import('./pages/public/Tutorials'));

// Print pages lazy-loaded — rarely accessed, keep them out of both bundles
const MedicationReport = lazyWithRetry(() => import('./pages/print/MedicationReport'), 'medication-report');
const LabResultsReport = lazyWithRetry(() => import('./pages/print/LabResultsReport'), 'lab-results-report');
const ImagingReport = lazyWithRetry(() => import('./pages/print/ImagingReport'), 'imaging-report');
const DischargeReport = lazyWithRetry(() => import('./pages/print/DischargeReport'), 'discharge-report');
const MedicalReport = lazyWithRetry(() => import('./pages/print/MedicalReport'), 'medical-report');
const ReferralReport = lazyWithRetry(() => import('./pages/print/ReferralReport'), 'referral-report');
const ExternalLabOrder = lazyWithRetry(() => import('./pages/print/ExternalLabOrder'), 'external-lab-order');
const ExternalImagingOrder = lazyWithRetry(() => import('./pages/print/ExternalImagingOrder'), 'external-imaging-order');
const ExternalProcedureOrder = lazyWithRetry(() => import('./pages/print/ExternalProcedureOrder'), 'external-procedure-order');
const AllReports = lazyWithRetry(() => import('./pages/print/AllReports'), 'all-reports');
const MaternalReport = lazyWithRetry(() => import('./pages/print/MaternalReport'), 'maternal-report');

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

// Location-aware error boundary: auto-resets when the route changes so a
// transient render error on one page doesn't brick the whole app.
function BoundaryWithLocation({ children }) {
  const location = useLocation();
  return <ErrorBoundary location={location}>{children}</ErrorBoundary>;
}

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    // Load saved language from localStorage
    const savedLang = localStorage.getItem('i18nextLng');
    if (savedLang) {
      i18n.changeLanguage(savedLang);
    }
  }, [i18n]);

  return (
    <Router>
      <PublicProviders>
        <ScrollToTop />
        <VisitTracker />
        <BoundaryWithLocation>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/patient-portal" element={<PatientPortal />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/our-story" element={<OurStory />} />
              <Route path="/portal/login" element={<PortalAuth />} />
              <Route path="/portal/register" element={<PortalAuth mode="register" />} />
              <Route path="/portal/dashboard" element={<PortalDashboard />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<Suspense fallback={<PageLoader />}><ForgotPassword /></Suspense>} />
              <Route path="/reset-password" element={<Suspense fallback={<PageLoader />}><ResetPassword /></Suspense>} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/payment/checkout" element={<PaymentCheckout />} />
              <Route path="/payment/callback" element={<PaymentSuccess />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/failed" element={<PaymentFailed />} />
              <Route path="/payment/pending" element={<PaymentPending />} />
              <Route path="/payment/dgateway/callback" element={<SecureCheckout />} />
              <Route path="/payment/dgateway/checkout" element={<SecureCheckout />} />
              {/* Legacy paths — keep old links (emails, bookmarks) working */}
              <Route path="/payment/flutterwave/callback" element={<SecureCheckout />} />
              <Route path="/payment/flutterwave/checkout" element={<SecureCheckout />} />
              <Route path="/demo/credentials" element={<DemoCredentials />} />
              <Route path="/demo/activate" element={<DemoActivation />} />
              {/* Tutorials page temporarily disabled — still has mockup data. Re-enable when ready. */}
              {/* <Route path="/tutorials" element={<Tutorials />} /> */}
              {/* Legal pages — redirect to the landing-page legal modal */}
              <Route path="/terms" element={<Navigate to="/?legal=terms" replace />} />
              <Route path="/privacy" element={<Navigate to="/?legal=privacy" replace />} />
              <Route path="/cookies" element={<Navigate to="/?legal=cookies" replace />} />

              {/* App Routes */}
              <Route path="/app/login" element={<Navigate to="/login" replace />} />
              <Route path="/app/join/:token" element={<AcceptInvitation />} />

              {/* Print report pages — standalone full-screen views (outside
                  MainLayout so printed output stays clean). Full paths here
                  let the bare /app/print fall through to MainLayout, where
                  the Print Center hub renders inside the standard app shell. */}
              <Route path="/app/print/medications/:patientId" element={<PrivateRoute><AppProviders><Suspense fallback={<PageLoader />}><MedicationReport /></Suspense></AppProviders></PrivateRoute>} />
              <Route path="/app/print/lab-results/:patientId" element={<PrivateRoute><AppProviders><Suspense fallback={<PageLoader />}><LabResultsReport /></Suspense></AppProviders></PrivateRoute>} />
              <Route path="/app/print/imaging/:patientId" element={<PrivateRoute><AppProviders><Suspense fallback={<PageLoader />}><ImagingReport /></Suspense></AppProviders></PrivateRoute>} />
              <Route path="/app/print/discharge/:patientId" element={<PrivateRoute><AppProviders><Suspense fallback={<PageLoader />}><DischargeReport /></Suspense></AppProviders></PrivateRoute>} />
              <Route path="/app/print/medical-report/:patientId" element={<PrivateRoute><AppProviders><Suspense fallback={<PageLoader />}><MedicalReport /></Suspense></AppProviders></PrivateRoute>} />
              <Route path="/app/print/referral/:patientId" element={<PrivateRoute><AppProviders><Suspense fallback={<PageLoader />}><ReferralReport /></Suspense></AppProviders></PrivateRoute>} />
              <Route path="/app/print/external-lab/:patientId" element={<PrivateRoute><AppProviders><Suspense fallback={<PageLoader />}><ExternalLabOrder /></Suspense></AppProviders></PrivateRoute>} />
              <Route path="/app/print/external-imaging/:patientId" element={<PrivateRoute><AppProviders><Suspense fallback={<PageLoader />}><ExternalImagingOrder /></Suspense></AppProviders></PrivateRoute>} />
              <Route path="/app/print/external-procedure/:patientId" element={<PrivateRoute><AppProviders><Suspense fallback={<PageLoader />}><ExternalProcedureOrder /></Suspense></AppProviders></PrivateRoute>} />
              <Route path="/app/print/all-reports/:patientId" element={<PrivateRoute><AppProviders><Suspense fallback={<PageLoader />}><AllReports /></Suspense></AppProviders></PrivateRoute>} />
              <Route path="/app/print/maternal/:patientId" element={<PrivateRoute><AppProviders><Suspense fallback={<PageLoader />}><MaternalReport /></Suspense></AppProviders></PrivateRoute>} />

              {/* App — MainLayout handles all sub-routing via AppRoutes.
                  MainLayout is lazy-loaded so all app modules stay out of
                  the public bundle; once loaded, navigation is instant. */}
              <Route path="/app/*" element={<PrivateRoute><AppProviders><MainLayout /></AppProviders></PrivateRoute>} />

              {/* 404 Catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BoundaryWithLocation>
      </PublicProviders>
    </Router>
  );
}

export default App;
